import React, { createContext, useContext, useState, useEffect } from 'react';
import { safeConsole } from '../utils/productionUtils';

// Animation presets and configurations
export const ANIMATION_PRESETS = {
  // Motion preferences
  motion: {
    none: {
      duration: 0,
      transition: 'linear',
      name: 'No Animation'
    },
    reduced: {
      duration: 200,
      transition: 'easeOut',
      name: 'Reduced Motion'
    },
    normal: {
      duration: 300,
      transition: 'easeInOut',
      name: 'Normal'
    },
    smooth: {
      duration: 500,
      transition: 'easeOut',
      name: 'Smooth'
    },
    bouncy: {
      duration: 600,
      transition: [0.68, -0.55, 0.265, 1.55],
      name: 'Bouncy'
    }
  },

  // Page transitions
  pageTransitions: {
    none: { name: 'None', type: 'none' },
    fade: { name: 'Fade', type: 'fade' },
    slide: { name: 'Slide', type: 'slide' },
    scale: { name: 'Scale', type: 'scale' },
    slideUp: { name: 'Slide Up', type: 'slideUp' }
  },

  // Hover effects
  hoverEffects: {
    none: { name: 'None', scale: 1, lift: 0 },
    subtle: { name: 'Subtle', scale: 1.02, lift: 2 },
    medium: { name: 'Medium', scale: 1.05, lift: 4 },
    pronounced: { name: 'Pronounced', scale: 1.08, lift: 8 }
  },

  // Loading animations
  loadingTypes: {
    spinner: { name: 'Spinner', type: 'spinner' },
    dots: { name: 'Dots', type: 'dots' },
    pulse: { name: 'Pulse', type: 'pulse' },
    skeleton: { name: 'Skeleton', type: 'skeleton' },
    progress: { name: 'Progress Bar', type: 'progress' }
  }
};

// Create animation context
const AnimationContext = createContext();

// Custom hook to use animations
export const useAnimation = () => {
  const context = useContext(AnimationContext);
  if (!context) {
    throw new Error('useAnimation must be used within an AnimationProvider');
  }
  return context;
};

// Animation provider component
export const AnimationProvider = ({ children }) => {
  const [animationSettings, setAnimationSettings] = useState({
    motionPreference: 'normal',
    pageTransitions: 'fade',
    hoverEffects: 'medium',
    loadingType: 'skeleton',
    celebrationLevel: 'medium',
    respectSystemPreferences: true
  });

  const [isReducedMotion, setIsReducedMotion] = useState(false);

  // Check system motion preferences
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setIsReducedMotion(mediaQuery.matches);

    const handleChange = (e) => {
      setIsReducedMotion(e.matches);
      if (animationSettings.respectSystemPreferences && e.matches) {
        setAnimationSettings(prev => ({ ...prev, motionPreference: 'reduced' }));
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [animationSettings.respectSystemPreferences]);

  // Load settings from localStorage
  useEffect(() => {
    const savedSettings = localStorage.getItem('footy-draft-animations');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setAnimationSettings(prev => ({ ...prev, ...parsed }));
      } catch (error) {
        safeConsole.warn('Failed to parse animation settings:', error);
      }
    }
  }, []);

  // Save settings to localStorage
  const updateAnimationSettings = (newSettings) => {
    const updated = { ...animationSettings, ...newSettings };
    setAnimationSettings(updated);
    localStorage.setItem('footy-draft-animations', JSON.stringify(updated));
  };

  // Get current motion preset
  const getCurrentMotionPreset = () => {
    if (isReducedMotion && animationSettings.respectSystemPreferences) {
      return ANIMATION_PRESETS.motion.reduced;
    }
    return ANIMATION_PRESETS.motion[animationSettings.motionPreference] || ANIMATION_PRESETS.motion.normal;
  };

  // Get current hover preset
  const getCurrentHoverPreset = () => {
    return ANIMATION_PRESETS.hoverEffects[animationSettings.hoverEffects] || ANIMATION_PRESETS.hoverEffects.medium;
  };

  // Animation utilities
  const createSpringConfig = (type = 'normal') => {
    const preset = ANIMATION_PRESETS.motion[type] || getCurrentMotionPreset();
    return {
      type: 'spring',
      damping: 25,
      stiffness: 300,
      duration: preset.duration / 1000
    };
  };

  const createTransition = (type = 'normal', delay = 0) => {
    const preset = ANIMATION_PRESETS.motion[type] || getCurrentMotionPreset();
    
    // Convert CSS easing to Framer Motion compatible values
    let ease = preset.transition;
    if (typeof ease === 'string') {
      switch (ease) {
        case 'ease-out':
          ease = 'easeOut';
          break;
        case 'ease-in':
          ease = 'easeIn';
          break;
        case 'ease-in-out':
          ease = 'easeInOut';
          break;
        case 'linear':
          ease = 'linear';
          break;
        default:
          ease = 'easeInOut';
      }
    }
    
    return {
      duration: preset.duration / 1000,
      delay,
      ease
    };
  };

  // Page transition variants
  const getPageTransitionVariants = () => {
    const transitionType = animationSettings.pageTransitions;
    const motionPreset = getCurrentMotionPreset();
    const duration = motionPreset.duration / 1000;

    const variants = {
      none: {
        initial: {},
        animate: {},
        exit: {}
      },
      fade: {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 }
      },
      slide: {
        initial: { x: 100, opacity: 0 },
        animate: { x: 0, opacity: 1 },
        exit: { x: -100, opacity: 0 }
      },
      scale: {
        initial: { scale: 0.9, opacity: 0 },
        animate: { scale: 1, opacity: 1 },
        exit: { scale: 1.1, opacity: 0 }
      },
      slideUp: {
        initial: { y: 50, opacity: 0 },
        animate: { y: 0, opacity: 1 },
        exit: { y: -50, opacity: 0 }
      }
    };

    const selectedVariant = variants[transitionType] || variants.fade;
    
    // Add transition timing to all variants
    Object.keys(selectedVariant).forEach(key => {
      if (selectedVariant[key].transition === undefined) {
        selectedVariant[key].transition = createTransition();
      }
    });

    return selectedVariant;
  };

  // Hover effect utilities
  const getHoverProps = (customScale, customLift) => {
    const preset = getCurrentHoverPreset();
    const scale = customScale || preset.scale;
    const lift = customLift || preset.lift;

    if (isReducedMotion && animationSettings.respectSystemPreferences) {
      return {}; // No hover effects for reduced motion
    }

    return {
      whileHover: {
        scale,
        y: -lift,
        transition: createTransition('normal')
      },
      whileTap: {
        scale: scale * 0.95,
        transition: createTransition('normal', 0)
      }
    };
  };

  // Loading animation props
  const getLoadingProps = (type = null) => {
    const loadingType = type || animationSettings.loadingType;
    const motionPreset = getCurrentMotionPreset();

    const loadingVariants = {
      spinner: {
        animate: {
          rotate: 360,
          transition: {
            duration: 1,
            repeat: Infinity,
            ease: 'linear'
          }
        }
      },
      dots: {
        animate: {
          scale: [1, 1.2, 1],
          transition: {
            duration: 0.6,
            repeat: Infinity,
            ease: 'easeInOut'
          }
        }
      },
      pulse: {
        animate: {
          opacity: [0.5, 1, 0.5],
          transition: {
            duration: 1.5,
            repeat: Infinity,
            ease: 'easeInOut'
          }
        }
      },
      skeleton: {
        animate: {
          backgroundPosition: ['200% 0', '-200% 0'],
          transition: {
            duration: 1.5,
            repeat: Infinity,
            ease: 'linear'
          }
        }
      },
      progress: {
        animate: {
          x: ['-100%', '100%'],
          transition: {
            duration: 1,
            repeat: Infinity,
            ease: 'linear'
          }
        }
      }
    };

    return loadingVariants[loadingType] || loadingVariants.skeleton;
  };

  // Success celebration animations
  const getCelebrationProps = (level = null) => {
    const celebrationLevel = level || animationSettings.celebrationLevel;

    const celebrations = {
      subtle: {
        initial: { scale: 1 },
        animate: {
          scale: [1, 1.05, 1],
          transition: { duration: 0.3, ease: 'easeOut' }
        }
      },
      medium: {
        initial: { scale: 1, rotate: 0 },
        animate: {
          scale: [1, 1.1, 1],
          rotate: [0, 5, -5, 0],
          transition: { duration: 0.6, ease: 'easeOut' }
        }
      },
      enthusiastic: {
        initial: { scale: 1, y: 0 },
        animate: {
          scale: [1, 1.2, 1],
          y: [0, -10, 0],
          rotate: [0, 10, -10, 0],
          transition: { duration: 0.8, ease: 'easeOut' }
        }
      }
    };

    return celebrations[celebrationLevel] || celebrations.medium;
  };

  // Stagger animation utilities
  const getStaggerProps = (index = 0, total = 1) => {
    const motionPreset = getCurrentMotionPreset();
    const staggerDelay = (motionPreset.duration / 1000) * 0.1;

    return {
      initial: { opacity: 0, y: 20 },
      animate: { opacity: 1, y: 0 },
      transition: {
        ...createTransition(),
        delay: index * staggerDelay
      }
    };
  };

  const value = {
    // Settings
    animationSettings,
    updateAnimationSettings,
    isReducedMotion,

    // Presets
    ANIMATION_PRESETS,
    getCurrentMotionPreset,
    getCurrentHoverPreset,

    // Utilities
    createSpringConfig,
    createTransition,
    getPageTransitionVariants,
    getHoverProps,
    getLoadingProps,
    getCelebrationProps,
    getStaggerProps
  };

  return (
    <AnimationContext.Provider value={value}>
      {children}
    </AnimationContext.Provider>
  );
};

export default AnimationContext;